import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { getEvents, APIError } from '../services/api';
import ProfileHeader from '../components/ui/ProfileHeader';
import EventCard from '../components/ui/EventCard';
import { EventGridSkeleton } from '../components/ui/EventCardSkeleton';
import EmptyState from '../components/ui/EmptyState';
import { motion } from 'framer-motion';

const TABS = [
  { key: 'my-events', label: 'My Events' },
  { key: 'attending', label: 'Attending' },
  { key: 'past', label: 'Past' },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.06 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
  },
};

function MyPage() {
  const { currentUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('my-events');
  // Renders the profile header as a visitor sees it (Sprint 2 #16).
  const [previewing, setPreviewing] = useState(false);
  const [myEvents, setMyEvents] = useState([]);
  const [attendingEvents, setAttendingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserEvents = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      const userId = currentUser?.id || currentUser?.ID;
      const response = await getEvents({ creator: userId });

      if (response.data) {
        const body = response.data;
        const allEvents = Array.isArray(body)
          ? body
          : (body.data || body.events || []);

        const now = new Date();

        // Separate into created (upcoming), attending, and past
        const upcoming = [];
        const past = [];

        allEvents.forEach((event) => {
          const startTime = event.StartTime || event.start_time;
          const eventDate = startTime ? new Date(startTime) : null;

          if (eventDate && eventDate < now) {
            past.push(event);
          } else {
            upcoming.push(event);
          }
        });

        setMyEvents(upcoming);
        setPastEvents(past);
      }

      // Fetch attending events separately if the API supports it
      try {
        const attendingResponse = await getEvents({ attendee: currentUser?.id || currentUser?.ID });
        if (attendingResponse.data) {
          const abody = attendingResponse.data;
          const events = Array.isArray(abody)
            ? abody
            : (abody.data || abody.events || []);
          setAttendingEvents(events.filter((e) => {
            const st = e.StartTime || e.start_time;
            return st ? new Date(st) >= new Date() : true;
          }));
        }
      } catch {
        // Attending events endpoint may not exist yet; silently ignore
      }
    } catch (err) {
      console.error('Failed to load user events:', err);
      setError(
        err instanceof APIError ? err.message : 'Failed to load your events'
      );
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchUserEvents();
    } else {
      setLoading(false);
    }
  }, [currentUser, fetchUserEvents]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      // Even on error, navigate away
      navigate('/login');
    }
  };

  // Not logged in
  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 rounded-2xl bg-primary-50 text-primary-500 flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h1 className="text-h2 text-surface-900 mb-3">Please log in to view your page</h1>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors duration-200"
        >
          Log In
        </Link>
      </div>
    );
  }

  // Build the profile object for ProfileHeader
  const profileData = {
    username: currentUser.username || currentUser.Username || '',
    display_name:
      currentUser.display_name ||
      currentUser.DisplayName ||
      [currentUser.first_name || currentUser.FirstName, currentUser.last_name || currentUser.LastName]
        .filter(Boolean)
        .join(' ') ||
      currentUser.username ||
      currentUser.Username ||
      'User',
    bio: currentUser.bio || currentUser.Bio || '',
    avatar: currentUser.avatar || currentUser.Avatar || currentUser.profile_image || '',
    interests: currentUser.interests || currentUser.Interests || [],
    events_count: myEvents.length + pastEvents.length,
    followers_count: currentUser.followers_count || currentUser.FollowersCount || 0,
    attending_count: attendingEvents.length,
  };

  // Current tab content
  const getTabEvents = () => {
    switch (activeTab) {
      case 'attending':
        return attendingEvents;
      case 'past':
        return pastEvents;
      case 'my-events':
      default:
        return myEvents;
    }
  };

  const getEmptyMessage = () => {
    switch (activeTab) {
      case 'attending':
        return {
          title: 'Not attending any events',
          description: 'Browse events to find something you would like to attend.',
          actionLabel: 'Explore Events',
          actionTo: '/events',
        };
      case 'past':
        return {
          title: 'No past events',
          description: 'Events you have hosted or attended will appear here after they conclude.',
        };
      case 'my-events':
      default:
        return {
          title: 'No events yet',
          description: 'You have not created any events yet. Start connecting with your community!',
          actionLabel: 'Create Your First Event',
          actionTo: '/create-cliqq',
        };
    }
  };

  const tabEvents = getTabEvents();
  const emptyMsg = getEmptyMessage();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Profile Header. `isOwner` drives the owner-only affordances (the Edit
          button), so previewing is a matter of rendering it as a visitor would
          see it -- Sprint 2 #16. */}
      <ProfileHeader profile={profileData} isOwner={!previewing} />

      {/* Preview toggle */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => setPreviewing((p) => !p)}
          aria-pressed={previewing}
          className="inline-flex items-center gap-2 px-4 py-2 text-body-sm font-semibold text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-surface-50 focus:outline-none focus:ring-2 focus:ring-primary-500/30 transition-colors duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {previewing ? 'Exit preview' : 'Preview as visitor'}
        </button>

        {previewing && (
          <span className="text-body-sm text-surface-500">
            This is how your profile looks to other people.
          </span>
        )}
      </div>

      {/* Logout Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 px-4 py-2 text-body-sm font-semibold text-surface-600 bg-white border border-surface-200 rounded-xl hover:bg-error-50 hover:text-error-600 hover:border-error-200 focus:outline-none focus:ring-2 focus:ring-error-500/30 transition-colors duration-200"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Log Out
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-surface-200">
        <nav className="flex gap-1" aria-label="Profile tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-3 text-body-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30 rounded-t-lg ${
                activeTab === tab.key
                  ? 'text-primary-600'
                  : 'text-surface-400 hover:text-surface-600'
              }`}
              aria-selected={activeTab === tab.key}
              role="tab"
            >
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="activeTabUnderline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500 rounded-full"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {loading ? (
          <EventGridSkeleton count={6} />
        ) : error ? (
          <div className="bg-error-50 border border-error-400/30 rounded-2xl p-6 text-center">
            <p className="text-body text-error-600 mb-4">{error}</p>
            <button
              type="button"
              onClick={fetchUserEvents}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-error-500 text-white text-body-sm font-semibold rounded-xl hover:bg-error-600 focus:outline-none focus:ring-2 focus:ring-error-500/40 transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        ) : tabEvents.length === 0 ? (
          <EmptyState
            icon={
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            title={emptyMsg.title}
            description={emptyMsg.description}
            actionLabel={emptyMsg.actionLabel}
            actionTo={emptyMsg.actionTo}
          />
        ) : (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            key={activeTab}
          >
            {tabEvents.map((event) => {
              const id = event.ID || event.id;
              return (
                <motion.div key={id} variants={cardVariants}>
                  <EventCard event={event} />
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default MyPage;
